import { Mutex } from 'async-mutex';

import { getClientEnv } from '@/config/clientEnv';
import { RefreshTokenData } from '@/types/Signup';
import { SuccessResponse } from '@/types/Success';
import { FetchBaseQueryArgs } from '@reduxjs/toolkit/dist/query/fetchBaseQuery';
import { BaseQueryFn, FetchArgs, fetchBaseQuery, FetchBaseQueryError } from '@reduxjs/toolkit/query';

let isReloading = false;
const mutex = new Mutex();

export const generateBaseQuseryWithReAuth = ({
  basePath,
  isAvatarProfileApis,
  useStrapiServer,
  ...args
}: Omit<FetchBaseQueryArgs, 'baseUrl'> & {
  basePath: string;
  useStrapiServer?: boolean;
  isAvatarProfileApis?: boolean;
  // eslint-disable-next-line sonarjs/cognitive-complexity
}) => {
  const { SERVER_URL, STRAPI_SERVER_URL, ORIGIN: origin, HOST: host, generateAuthRefreshURL } = getClientEnv();

  const FINAL_SERVER_URL = isAvatarProfileApis ? SERVER_URL?.replace('/api', '') : SERVER_URL;

  const baseQuery = fetchBaseQuery({
    ...args,
    baseUrl: `${useStrapiServer ? STRAPI_SERVER_URL : FINAL_SERVER_URL}${
      !basePath.length ? '' : basePath.startsWith('/') ? basePath : '/' + basePath
    }`,
    ...(useStrapiServer ? {} : { credentials: 'same-origin' }),

    prepareHeaders: headers => {
      if (useStrapiServer) return headers;

      const ORIGIN = origin || globalThis?.window?.location?.origin || '';
      const HOST = host || globalThis?.window?.location?.host || '';

      headers.append('Origin', ORIGIN);
      headers.append('Host', HOST);
      headers.append('__HOST', HOST);
      headers.append('__Origin', ORIGIN);

      if (globalThis?.window?.__FORWARDED_HEADERS__) {
        try {
          const forwardedHeaders = globalThis.window.__FORWARDED_HEADERS__;
          Object.entries(forwardedHeaders).forEach(([key, value]) => {
            if (value !== null) {
              headers.append(key, value as string);
            }
          });
        } catch (error) {
          console.error('Error appending forwarded headers:', error);
        }
      }

      return headers;
    },
  });

  const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
    args,
    api,
    extraOptions
  ) => {
    await mutex.waitForUnlock();
    let result = await baseQuery(args, api, extraOptions);

    if (result.error && result.error.status === 401) {
      if (!mutex.isLocked()) {
        const release = await mutex.acquire();
        try {
          const refreshResp = await fetch(generateAuthRefreshURL(), {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
              'Content-Type': 'application/json',
              __origin: origin || '',
              __host: host || '',
              ...(globalThis?.window?.__FORWARDED_HEADERS__ ? globalThis?.window.__FORWARDED_HEADERS__ : {}),
            },
          });

          if (refreshResp.status === 401) throw new Error('Refresh token is invalid or expired. BE returned 401.');

          const refreshData = (await refreshResp.json()) as SuccessResponse<RefreshTokenData>;

          if (refreshData.success && refreshData.data) {
            result = await baseQuery(args, api, extraOptions);
          }
        } catch (err) {
          console.error(err, 'baseQueryReauth: error occurred during token refresh');

          // Redirect to login page
          if (typeof globalThis?.window !== 'undefined') {
            const currentHost = window.location.host;
            const isBackoffice = currentHost.includes('backoffice');
            const logoutUrl = isBackoffice
              ? '/logout'
              : `/${window.PAWTREE_CURRENT_WEBALIAS || 'pawtree'}/account/logout`;

            window.location.href = logoutUrl;
          }
        } finally {
          release();
        }
      } else {
        await mutex.waitForUnlock();
        result = await baseQuery(args, api, extraOptions);
      }
    }

    if (result.error && result.error.status === 401 && typeof window !== 'undefined' && !isReloading) {
      isReloading = true;
      window.location.reload();
      return result;
    }

    let urlToRefreshCustomer: string | undefined = undefined;

    const returnURL =
      (result.data as any)?.data?.ReplicatedSiteReturnURL || (result.data as any)?.ReplicatedSiteReturnURL;

    const backofficeReturnURL =
      (result.data as any)?.data?.BackofficeReturnURL || (result.data as any)?.BackofficeReturnURL;

    if (backofficeReturnURL) urlToRefreshCustomer = backofficeReturnURL;

    if (returnURL) {
      urlToRefreshCustomer = `${window.PAWTREE_CURRENT_WEBALIAS || 'pawtree'}/${returnURL}`;
    }

    if (urlToRefreshCustomer) {
      try {
        const refreshCustomerCall = await fetch(urlToRefreshCustomer);
        await refreshCustomerCall.json();
        console.log('customer refreshed successfully');
      } catch (error) {
        console.log('an error occured while refreshing the customer', error);
      }
    }

    return result;
  };

  const baseQueryForStrapi: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
    args,
    api,
    extraOptions
  ) => {
    return await baseQuery(args, api, extraOptions);
  };

  return useStrapiServer ? baseQueryForStrapi : baseQueryWithReauth;
};
